import { forwardRef } from 'react';
import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@mui/material';

/**
 * TextField component props extending MUI TextField with custom value handling.
 */
export interface TextFieldProps extends Omit<MuiTextFieldProps, 'value' | 'onChange'> {
  /**
   * The value of the input. Can be string or number.
   * For number inputs, zero values will be displayed as empty string.
   */
  value?: string | number;

  /**
   * Callback fired when the value changes.
   * For number inputs (type="number"), returns:
   * - A number when the input is a valid finite numeric value
   * - A string for partial/intermediate numeric input (e.g., "-", "1e", "3.")
   * - An empty string when cleared
   * For text inputs, returns a string.
   *
   * @param value - The new value (string for text inputs, number/string/empty for number inputs)
   */
  onChange?: (value: string | number) => void;

  /**
   * If true, automatically selects the input text when focused.
   * This improves mobile UX for quick edits by allowing users to immediately
   * replace the existing value by typing.
   *
   * @default true
   */
  autoSelectOnFocus?: boolean;
}

/**
 * TextField component - A text input field that extends MUI TextField with mobile-optimized UX enhancements.
 *
 * **Features:**
 * - Auto-select text on focus (mobile UX improvement)
 * - Smart number value handling (converts types automatically)
 * - Supports all MUI TextField variants and props
 * - Forward ref support for imperative access
 * - Full accessibility support
 *
 * **Common Use Cases:**
 * - Text input fields
 * - Number input with type conversion
 * - Multiline text areas
 * - Form fields with validation
 * - Fields with units/adornments
 *
 * @example
 * ```tsx
 * // Basic text input
 * <TextField label="Name" value={name} onChange={setName} />
 *
 * // Number input with units
 * <TextField
 *   label="Height"
 *   type="number"
 *   value={height}
 *   onChange={setHeight}
 *   InputProps={{
 *     endAdornment: <InputAdornment position="end">cm</InputAdornment>
 *   }}
 * />
 *
 * // Multiline text area
 * <TextField
 *   label="Observations"
 *   multiline
 *   rows={4}
 *   value={notes}
 *   onChange={setNotes}
 * />
 *
 * // With validation error
 * <TextField
 *   label="Email"
 *   error={!isValidEmail}
 *   helperText={!isValidEmail ? "Invalid email address" : ""}
 *   value={email}
 *   onChange={setEmail}
 * />
 * ```
 */
export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  ({ value, onChange, autoSelectOnFocus = true, onFocus, type, sx, ...props }, ref) => {
    // Display empty string for zero values when type is number
    // This provides better UX as users expect to see an empty field rather than "0"
    const displayValue = type === 'number' && value === 0 ? '' : value;

    /**
     * Handle value changes with smart number conversion.
     * For number inputs, converts valid numeric strings to numbers,
     * but preserves partial/intermediate input (e.g., "-", "1e", "3.") as strings.
     * Preserves empty string to allow clearing the field.
     */
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      if (onChange) {
        if (type === 'number') {
          // Handle empty string
          if (newValue === '') {
            onChange('');
          } else {
            // Try to convert to number
            const numValue = Number(newValue);
            // Only convert to number if it's a valid finite number
            // Otherwise preserve the string for partial input like "-", "1e", "3."
            onChange(Number.isFinite(numValue) ? numValue : newValue);
          }
        } else {
          onChange(newValue);
        }
      }
    };

    /**
     * Merge auto-select behavior with custom onFocus handler.
     * Auto-selects text when focused (if enabled) for quick editing,
     * then calls any custom focus handler provided by the consumer.
     */
    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (autoSelectOnFocus) {
        event.target.select();
      }

      if (onFocus) {
        onFocus(event);
      }
    };

    return (
      <MuiTextField
        ref={ref}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        type={type}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
          },
          ...sx,
        }}
        {...props}
      />
    );
  }
);

TextField.displayName = 'TextField';
